import React, { memo, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  MenuItem,
  Paper,
  Popover,
  TextField,
  Tooltip,
  Typography,
  FormControlLabel,
  Switch,
  Chip,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ArrowDropDownOutlinedIcon from "@mui/icons-material/ArrowDropDownOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { styled } from "@mui/material/styles";
import debounce from "lodash/debounce";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import SectionLabel from "../../widgets/SectionLabel";
import { GlassButton } from "../../themes/UiElements";

/* --------------------------------- styling --------------------------------- */
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[0],
  backgroundColor: theme.palette.background.paper,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-root": { borderRadius: theme.shape.borderRadius },
  "& .MuiInputLabel-root": { fontSize: "0.9rem" },
  width: "100%",
}));

/* ---------------------- pure memo child components ------------------------- */

const MenuSection = memo(function MenuSection({
  title,
  items,
  type,
  disabledCheck,
  onSelect,
}) {
  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        {title}
      </Typography>
      {items.map((itm) => (
        <MenuItem
          key={`${type}-${itm.name}`}
          disabled={disabledCheck(itm)}
          onClick={() => onSelect(type, itm)}
        >
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {itm.name}
            </Typography>
            {itm.subtext && (
              <Typography variant="caption" color="text.secondary">
                {itm.subtext}
              </Typography>
            )}
          </Box>
        </MenuItem>
      ))}
    </Box>
  );
});

const InputBlock = memo(function InputBlock({
  button,
  meta,
  onFieldChange,
  onRemove,
  isDragging,
  dragHandleProps,
}) {
  const update = (field) => (e) =>
    onFieldChange(button.id, field, e.target.value);

  const toggleDynamic = () => {
    onFieldChange(button.id, "isDynamic", !button.isDynamic);
  };

  const addVariable = () => {
    // Only allow one variable for URL buttons
    if (button.urlVariable) return;
    onFieldChange(button.id, "urlVariable", { name: "", example: "" });
  };

  const updateVariable = (field, value) => {
    const variable = { ...(button.urlVariable || {}), [field]: value };
    onFieldChange(button.id, "urlVariable", variable);
  };

  const removeVariable = () => {
    onFieldChange(button.id, "urlVariable", null);
  };

  return (
    <Box
      sx={{
        borderRadius: 2,
        mt: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        backgroundColor: isDragging ? "action.hover" : "transparent",
        transition: "background-color 0.2s",
        p: 2,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <Box
          {...dragHandleProps}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "grab",
            color: "text.secondary",
            "&:active": { cursor: "grabbing" },
            pt: 1,
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>

        <StyledTextField
          label="Button text"
          value={button.text}
          onChange={update("text")}
          size="small"
        />

        <Tooltip title="Remove">
          <Box>
            <IconButton
              onClick={() => onRemove(button)}
              sx={{ color: "error.main", ml: "auto" }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        </Tooltip>
      </Box>

      {meta.type === "url" && (
        <Box sx={{ pl: 5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={button.isDynamic || false}
                  onChange={toggleDynamic}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  Dynamic URL (with variables)
                </Typography>
              }
            />
            {button.isDynamic && (
              <Chip
                label="Dynamic"
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>

          <StyledTextField
            label="URL"
            value={button.url || ""}
            onChange={update("url")}
            size="small"
            helperText={
              button.isDynamic
                ? "Use {{1}}, {{2}}, etc. for variables"
                : "Enter a static URL"
            }
          />

          {button.isDynamic && (
            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Variable (Max 1)
                </Typography>
                {!button.urlVariable && (
                  <Button
                    size="small"
                    startIcon={<AddOutlinedIcon />}
                    onClick={addVariable}
                    sx={{ textTransform: "none" }}
                  >
                    Add Variable
                  </Button>
                )}
              </Box>

              {button.urlVariable && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-start",
                    mb: 1,
                    p: 1.5,
                    backgroundColor: "action.hover",
                    borderRadius: 1,
                  }}
                >
                  <Chip label="{{1}}" size="small" sx={{ mt: 1 }} />
                  <StyledTextField
                    label="Variable Name"
                    value={button.urlVariable.name || ""}
                    onChange={(e) => updateVariable("name", e.target.value)}
                    size="small"
                    placeholder="e.g., mob, user_id"
                  />
                  <StyledTextField
                    label="Example Value"
                    value={button.urlVariable.example || ""}
                    onChange={(e) => updateVariable("example", e.target.value)}
                    size="small"
                    placeholder="e.g., 9538077099"
                  />
                  <IconButton
                    size="small"
                    onClick={removeVariable}
                    sx={{ color: "error.main", mt: 0.5 }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {!button.urlVariable && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}
                >
                  No variable added yet. Click "Add Variable" to define one
                  variable for your URL.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      {meta.type === "phone" && (
        <Box sx={{ pl: 5 }}>
          <StyledTextField
            label="Phone number"
            value={button.phone || ""}
            onChange={update("phone")}
            size="small"
          />
        </Box>
      )}

      {meta.type === "code" && (
        <Box sx={{ pl: 5 }}>
          <StyledTextField
            label="Offer code"
            value={button.code || ""}
            onChange={update("code")}
            size="small"
          />
        </Box>
      )}

      {meta.type === "flow" && (
        <Box sx={{ pl: 5 }}>
          <StyledTextField
            label="Flow ID"
            helperText={
              <Typography variant="body1">
                <a href="/home/flows" target="_blank">
                  Click here
                </a>{" "}
                for the flow id
              </Typography>
            }
            value={button.flowId || ""}
            onChange={update("flowId")}
            size="small"
          />
        </Box>
      )}
    </Box>
  );
});

/* ------------------------- main controlled component ----------------------- */

function ButtonTemplateCreator({
  value,
  onChange,
  /* ui / limits props with defaults */
  initialCTAItems = [
    {
      name: "Visit website",
      maxButtons: 2,
      subtext: "2 buttons max",
      type: "url",
    },
    {
      name: "Call Phone",
      maxButtons: 1,
      subtext: "1 button max",
      type: "phone",
    },
    {
      name: "Complete flow",
      maxButtons: 1,
      subtext: "1 button max",
      type: "flow",
    },
    {
      name: "Copy offer code",
      maxButtons: 1,
      subtext: "1 button max",
      type: "code",
    },
  ],
  quickReplyLabel = "Quick-reply",
  ctaLabel = "Call-to-action",
  addButtonLabel = "Add button",
  maxQuickReplies = 6,
}) {
  /* pop-over anchor */
  const [anchorEl, setAnchorEl] = React.useState(null);
  const openMenu = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  /* derive quick/cta arrays & counts from controlled value */
  const quickReplies = value.filter((b) => b.group === "quickReply");
  const ctaButtons = value.filter((b) => b.group === "cta");

  const ctaCounts = React.useMemo(() => {
    const counts = Object.fromEntries(initialCTAItems.map((i) => [i.name, 0]));
    ctaButtons.forEach((b) => {
      counts[b.name] = (counts[b.name] || 0) + 1;
    });
    return counts;
  }, [ctaButtons, initialCTAItems]);

  /* debounced onChange for field edits */
  const debouncedEmit = useRef(debounce(onChange, 0)).current;

  /* --------------- handlers --------------- */
  const addButton = useCallback(
    (type, itemMeta) => {
      if (type === "quickReply" && quickReplies.length >= maxQuickReplies)
        return;
      if (type === "cta" && ctaCounts[itemMeta.name] >= itemMeta.maxButtons)
        return;
      const id = `${type}-${itemMeta.name}-${Date.now()}`;
      const newBtn = {
        id,
        group: type,
        name: itemMeta.name,
        type: itemMeta.type || "text",
        text: itemMeta.name,
        isDynamic: false,
        urlVariable: null, // Single variable object: { name, example }
      };
      onChange([...value, newBtn]);
      closeMenu();
    },
    [ctaCounts, maxQuickReplies, onChange, quickReplies.length, value]
  );

  const removeButton = useCallback(
    (btn) => {
      onChange(value.filter((b) => b.id !== btn.id));
    },
    [onChange, value]
  );

  const updateField = useCallback(
    (id, field, fieldValue) => {
      const next = value.map((b) =>
        b.id === id ? { ...b, [field]: fieldValue } : b
      );
      debouncedEmit(next);
    },
    [debouncedEmit, value]
  );

  const handleDragEnd = useCallback(
    (result) => {
      if (!result.destination) return;

      const { source, destination } = result;

      // Reorder the entire value array
      const reordered = Array.from(value);
      const [removed] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, removed);

      onChange(reordered);
    },
    [onChange, value]
  );

  // Helper to get meta info for any button
  const getButtonMeta = useCallback(
    (button) => {
      if (button.group === "quickReply") {
        return { type: "text" };
      }
      return initialCTAItems.find((i) => i.name === button.name) || {};
    },
    [initialCTAItems]
  );

  /* ----------------------------- render ----------------------------- */
  return (
    <StyledPaper sx={{ maxWidth: 760, mx: "auto" }}>
      <GlassButton
        variant="contained"
        startIcon={<AddOutlinedIcon />}
        endIcon={<ArrowDropDownOutlinedIcon />}
        onClick={openMenu}
        sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
      >
        {addButtonLabel}
      </GlassButton>

      {/* pop-over menu */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { borderRadius: 2, width: 320 } }}
      >
        <MenuSection
          title={quickReplyLabel}
          type="quickReply"
          items={[
            {
              name: "Quick reply",
              maxButtons: maxQuickReplies,
              type: "text",
            },
          ]}
          disabledCheck={() => quickReplies.length >= maxQuickReplies}
          onSelect={addButton}
        />
        <Divider />
        <MenuSection
          title={ctaLabel}
          type="cta"
          items={initialCTAItems}
          disabledCheck={(itm) => ctaCounts[itm.name] >= itm.maxButtons}
          onSelect={addButton}
        />
      </Popover>

      <Box sx={{ mt: 2 }}>
        <SectionLabel>Selected ({value.length})</SectionLabel>

        {value.length > 0 && (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="all-buttons">
              {(provided, snapshot) => (
                <List
                  dense
                  disablePadding
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    backgroundColor: snapshot.isDraggingOver
                      ? "action.hover"
                      : "transparent",
                    borderRadius: 2,
                    transition: "background-color 0.2s",
                    p: 1,
                  }}
                >
                  {value.map((btn, index) => (
                    <Draggable key={btn.id} draggableId={btn.id} index={index}>
                      {(provided, snapshot) => (
                        <ListItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          disableGutters
                          sx={{ display: "block" }}
                        >
                          <InputBlock
                            button={btn}
                            meta={getButtonMeta(btn)}
                            onFieldChange={updateField}
                            onRemove={removeButton}
                            isDragging={snapshot.isDragging}
                            dragHandleProps={provided.dragHandleProps}
                          />
                        </ListItem>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </List>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Box>
    </StyledPaper>
  );
}

export default memo(ButtonTemplateCreator);
